import { StaticImageData } from "next/image"
import {
    sidebarPlanningIcon,
    sidebarMemberIcon,
    sidebarProfileIcon,
    sidebarReportIcon,
    sidebarSettingIcon,
    sidebarWorkOrderIcon,
    sidebarWorkScopeIcon,
    sidebarPartnersIcon,
    sidebarToolsSuppliesIcon,
    userImage2,
} from "./images"
import { DASHBOARD_ROUTE } from "./pages-routes"
import { UserRole } from "./user-roles"

export type SidebarLinkType = (typeof SIDEBAR_LINKS)[number] & { isRegionAware: boolean }

export const SIDEBAR_LINKS = [
    {
        name: "Reports",
        url: DASHBOARD_ROUTE.reports,
        icon: sidebarReportIcon,
        isRegionAware: true,
    },
    {
        name: "Hub",
        url: DASHBOARD_ROUTE.base,
        icon: sidebarProfileIcon,
        isRegionAware: true,
    },
    {
        name: "Homeowners",
        url: DASHBOARD_ROUTE.dashboard, // dashboard = "/homeowners"
        icon: sidebarPlanningIcon,
        isRegionAware: true,
    },
    {
        name: "Work Orders",
        url: DASHBOARD_ROUTE.workOrders,
        icon: sidebarWorkOrderIcon,
        isRegionAware: true,
    },
    {
        name: "Volunteers",
        url: DASHBOARD_ROUTE.volunteers,
        icon: sidebarMemberIcon,
        isRegionAware: true,
    },
    {
        name: "Partners",
        url: DASHBOARD_ROUTE.partners,
        icon: sidebarPartnersIcon,
        isRegionAware: true,
    },
    {
        name: "Admin",
        url: DASHBOARD_ROUTE.admin,
        icon: sidebarToolsSuppliesIcon, // Using tools/supplies icon as placeholder
        isRegionAware: true,
    },
    {
        name: "Tools/Supplies",
        url: DASHBOARD_ROUTE.toolsSupplies,
        icon: sidebarToolsSuppliesIcon,
        isRegionAware: false,
    },
    {
        name: "Settings",
        url: DASHBOARD_ROUTE.settings,
        icon: sidebarSettingIcon,
        isRegionAware: false,
    },
    {
        name: "Migration",
        url: DASHBOARD_ROUTE.migration,
        icon: sidebarWorkScopeIcon, // Using work scope icon as placeholder
        isRegionAware: true,
    },
] as const

export type SidebarLinkName = (typeof SIDEBAR_LINKS)[number]["name"]

export const getSidebarLinks = (name: UserRole, showVolunteersLink?: boolean) => {
    const filterVolunteersForGroupContactRoles = (links: readonly SidebarLinkType[]) => {
        if (name !== UserRole.VOLUNTEER && name !== UserRole.TEAM_LEADER && name !== UserRole.PARTNER) return links
        if (showVolunteersLink === true) return links
        return links.filter(({ name: n }) => n !== "Volunteers")
    }
    switch (name) {
        case UserRole.ADMIN:
            // Admin sees Admin dashboard (with tools-supplies, migration, partners tabs)
            // Remove individual Tools/Supplies, Migration, Partners, and Settings links
            return SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Migration" &&
                    name !== "Partners" &&
                    name !== "Settings"
            )

        case UserRole.BASE_LEADER:
            // Base Leader sees Admin dashboard (with tools-supplies, migration, partners tabs)
            // Remove individual Tools/Supplies, Migration, Partners, and Settings links
            return SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Migration" &&
                    name !== "Partners" &&
                    name !== "Settings"
            )
            
        case UserRole.TEAM_LEADER:
            // Team Leader sees Partners (read-only), Volunteers only if group contact
            return filterVolunteersForGroupContactRoles(SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Admin" &&
                    name !== "Migration" &&
                    name !== "Settings"
            ))

        case UserRole.VOLUNTEER:
            // Volunteers see Partners (read-only), Volunteers only if group contact
            return filterVolunteersForGroupContactRoles(SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Admin" &&
                    name !== "Migration" &&
                    name !== "Settings"
            ))
        case UserRole.HOMEOWNER:
            // Homeowners see Partners (read-only) but not Admin dashboard
            return SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Volunteers" &&
                    name !== "Admin" &&
                    name !== "Migration" &&
                    name !== "Work Orders" &&
                    name !== "Settings"
            )

        case UserRole.PARTNER:
            // Partners see Partners (read-only), Volunteers only if group contact
            return filterVolunteersForGroupContactRoles(SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Admin" &&
                    name !== "Migration" &&
                    name !== "Work Orders" &&
                    name !== "Settings"
            ))

        case UserRole.Supporter:
            // Supporters see Partners (read-only) but not Admin dashboard, not Volunteers
            return SIDEBAR_LINKS.filter(
                ({ name }) =>
                    name !== "Tools/Supplies" &&
                    name !== "Volunteers" &&
                    name !== "Admin" &&
                    name !== "Migration" &&
                    name !== "Work Orders" &&
                    name !== "Settings"
            )
        default:
            // Default fallback - return all links for unknown roles (Settings hidden)
            return SIDEBAR_LINKS.filter(({ name }) => name !== "Settings")
    }
}

const MOBILE_SIDEBAR_NAMES: SidebarLinkName[] = ["Reports", "Hub"]

export const MOBILE_SIDEBAR_LINKS = SIDEBAR_LINKS.filter(({ name }) =>
    MOBILE_SIDEBAR_NAMES.includes(name)
)

export type DelegateTo = {
    name: string
    role: string
    avatar: string | StaticImageData
}

export type WorkOrder = {
    homeOwner: string
    streetAddress: string
    workScopeName: string
    delegateTo: DelegateTo
    status: string // Can be "Completed" or a progress percentage like "98%"
}

export const WORK_ORDER_LIST: WorkOrder[] = [
    {
        homeOwner: "Victoria Smith",
        streetAddress: "10500 Canal Street",
        workScopeName: "Drywall",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "Completed",
    },
    {
        homeOwner: "Tracy Montjoy-Collins",
        streetAddress: "10506 Canal Street",
        workScopeName: "Electrical",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "98%", // Progress percentage
    },
    {
        homeOwner: "Denise Robertson",
        streetAddress: "10430 Canal Street",
        workScopeName: "Drywall",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "12%", // Progress percentage
    },
    {
        homeOwner: "Ryan Brummoms",
        streetAddress: "10507 Canal Street",
        workScopeName: "Electrical",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "25%", // Progress percentage
    },
    {
        homeOwner: "John Harrell",
        streetAddress: "10530 Canal Street",
        workScopeName: "Drywall",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "75%", // Progress percentage
    },
    {
        homeOwner: "Robert Krause",
        streetAddress: "10560 Canal Street",
        workScopeName: "Electrical",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "Completed",
    },
    {
        homeOwner: "Randal Milton",
        streetAddress: "10580 Canal Street",
        workScopeName: "Drywall",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "25%", // Progress percentage
    },
    {
        homeOwner: "Elizabeth Golden",
        streetAddress: "10650 Canal Street",
        workScopeName: "Electrical",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "75%", // Progress percentage
    },
    {
        homeOwner: "Roxie Williams",
        streetAddress: "10680 Canal Street",
        workScopeName: "Drywall",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "12%", // Progress percentage
    },
    {
        homeOwner: "Sharon Wilcher",
        streetAddress: "10506 Canal Street",
        workScopeName: "Electrical",
        delegateTo: {
            name: "John Doe",
            role: "Volunteer",
            avatar: userImage2,
        },
        status: "Completed",
    },
]

export type HomeOwner = {
    homeOwner: string
    phoneNumber: string
    streetAddress: string
    demographics: string[]
}

export const HOME_OWNERS_LIST: HomeOwner[] = [
    {
        // id: 1,
        homeOwner: "Victoria Smith",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10500 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 2,
        homeOwner: "Tracy Montjoy-Collins",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10506 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 3,
        homeOwner: "Denise Robertson",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10430 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 4,
        homeOwner: "Ryan Brummoms",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10507 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 5,
        homeOwner: "John Harrell",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10530 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 6,
        homeOwner: "Robert Krause",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10560 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 7,
        homeOwner: "Randal Milton",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10580 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 8,
        homeOwner: "Elizabeth Golden",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10650 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 9,
        homeOwner: "Roxie Williams",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10680 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
    {
        // id: 10,
        homeOwner: "Sharon Wilcher",
        phoneNumber: "+1 336 430 8944",
        streetAddress: "10506 Canal Street",
        demographics: [
            "60+ Elderly",
            "Veteran",
            "No Home Insurance",
            "No Flood Insurance",
        ],
    },
]
